import { Grid, Group, Input, Select, Text, TextInput, ThemeIcon } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconLock } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import countries from 'i18n-iso-countries';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadCommunicationLocales } from 'modules/Settings/Configuration/api';
import { useEffect } from 'react';
import { CommunicationLocaleSchema } from 'types/locale';
import { z } from 'zod/v4';

import {
  type UpdateCompanyProfileVariables,
  useLoadCompanyProfile,
  useUpdateCompanyProfile,
} from './api';

const CompanyProfileFormSchema = z.object({
  name: z.string().min(1),
  companyEmail: z.email(),
  locale: CommunicationLocaleSchema,
  phoneNumber: z.string(),
  address: z.object({
    streetName: z.string().min(1),
    streetNumber: z.string().min(1),
    streetBox: z.string(),
    zipCode: z.string().min(1),
    city: z.string().min(1),
    countryCode: z.string().min(1),
  }),
});

type CompanyProfileFormValues = z.infer<typeof CompanyProfileFormSchema>;

const INITIAL_VALUES: CompanyProfileFormValues = {
  name: '',
  companyEmail: '',
  locale: 'fr',
  phoneNumber: '',
  address: {
    streetName: '',
    streetNumber: '',
    streetBox: '',
    zipCode: '',
    city: '',
    countryCode: '',
  },
};

// Read-only display formatting: split the two-letter country prefix from the
// rest of the number with a space (e.g. "BE0010085426" -> "BE 0010085426").
const formatVatNumber = (value: string) => {
  const match = value.trim().match(/^([A-Za-z]{2})(.+)$/);
  return match ? `${match[1].toUpperCase()} ${match[2]}` : value;
};

export const CompanyProfileCard = () => {
  const { communicationLanguages } = useLoadCommunicationLocales();
  const { t, currentLang } = useTranslation();
  const { companyProfile } = useLoadCompanyProfile();
  const { updateCompanyProfile, isUpdateCompanyProfileLoading } = useUpdateCompanyProfile();

  const form = useForm<CompanyProfileFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(CompanyProfileFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded profile into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!companyProfile) return;

    const values: CompanyProfileFormValues = {
      name: companyProfile.name,
      companyEmail: companyProfile.companyEmail,
      locale: companyProfile.locale,
      phoneNumber: companyProfile.phoneNumber,
      address: {
        streetName: companyProfile.address.streetName,
        streetNumber: companyProfile.address.streetNumber,
        streetBox: companyProfile.address.streetBox ?? '',
        zipCode: companyProfile.address.zipCode,
        city: companyProfile.address.city,
        countryCode: companyProfile.address.countryCode,
      },
    };
    form.setInitialValues(values);
    form.setValues(values);
  }, [companyProfile]);

  const countryOptions = Object.entries(countries.getNames(currentLang)).map(([value, label]) => ({
    value,
    label,
  }));

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdateCompanyProfileVariables = {};
    if (form.isDirty('name')) payload.name = values.name;
    if (form.isDirty('companyEmail')) payload.companyEmail = values.companyEmail;
    if (form.isDirty('locale')) payload.locale = values.locale;
    if (form.isDirty('phoneNumber')) payload.phoneNumber = values.phoneNumber;
    if (form.isDirty('address')) payload.addressAttributes = values.address;

    updateCompanyProfile(payload, { onSuccess: () => form.resetDirty() });
  };

  return (
    <Panel
      title={t(i18nKeys.SETTINGS.COMPANY.PROFILE)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateCompanyProfileLoading}
          hideIcon
        />
      }
    >
      <Grid my="md">
        <Grid.Col span={6}>
          <TextInput label={t(i18nKeys.FORM.COMPANY.LEGAL_NAME)} {...form.getInputProps('name')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t(i18nKeys.EMAIL)}
            type="email"
            {...form.getInputProps('companyEmail')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t(i18nKeys.FORM.ADDRESS.STREET)}
            {...form.getInputProps('address.streetName')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput label={t(i18nKeys.NUMBER)} {...form.getInputProps('address.streetNumber')} />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            label={t(i18nKeys.FORM.ADDRESS.BOX)}
            {...form.getInputProps('address.streetBox')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            label={t(i18nKeys.FORM.ADDRESS.POSTAL_CODE)}
            {...form.getInputProps('address.zipCode')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t(i18nKeys.FORM.ADDRESS.CITY)}
            {...form.getInputProps('address.city')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label={t(i18nKeys.FORM.ADDRESS.COUNTRY)}
            searchable
            data={countryOptions}
            {...form.getInputProps('address.countryCode')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label={t(i18nKeys.PHONES)} {...form.getInputProps('phoneNumber')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label={t(i18nKeys.FORM.COMPANY.LANG)}
            allowDeselect={false}
            data={communicationLanguages.map(({ value, description }) => ({
              value,
              label: description,
            }))}
            {...form.getInputProps('locale')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Input.Wrapper label={t(i18nKeys.VAT_NUMBER)}>
            <Group
              gap="xs"
              wrap="nowrap"
              px="sm"
              py={6}
              bg="var(--mantine-color-gray-light)"
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              <ThemeIcon variant="transparent" color="gray" size="sm">
                <IconLock size={16} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {companyProfile ? formatVatNumber(companyProfile.vatNumber) : ''}
              </Text>
            </Group>
          </Input.Wrapper>
        </Grid.Col>
      </Grid>
    </Panel>
  );
};
